import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';
import { AlarmClock, Loader2, Check, Trash2, Car, Calendar, Gauge, Package, CheckSquare, ChevronDown, ChevronRight } from 'lucide-react';
import { useEscapeToHome } from '@/hooks/useKeyboard';

export default function RemindersPage() {
  useEscapeToHome();
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [collapsed, setCollapsed] = useState({});

  const fetchReminders = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/reminders/pending-summary', { params: { status_filter: filter } });
      setGroups(data.groups || []);
      setTotal(data.total || 0);
    } catch {}
    setLoading(false);
  }, [filter]);

  useEffect(() => { fetchReminders(); }, [fetchReminders]);

  const markDone = async (id) => {
    try { await api.put(`/reminders/${id}`, { status: 'done' }); toast.success('Marked as done'); fetchReminders(); }
    catch { toast.error('Error'); }
  };
  const deleteReminder = async (id) => {
    try { await api.delete(`/reminders/${id}`); toast.success('Deleted'); fetchReminders(); }
    catch { toast.error('Error'); }
  };

  const toggleGroup = (key) => setCollapsed(p => ({ ...p, [key]: !p[key] }));

  const typeIcons = { vehicle: Car, asset: Package, task: CheckSquare, general: AlarmClock };
  const typeColors = { vehicle: 'bg-cyan-50 dark:bg-cyan-950/30 border-cyan-200 dark:border-cyan-800', asset: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800', task: 'bg-violet-50 dark:bg-violet-950/30 border-violet-200 dark:border-violet-800', general: 'bg-zinc-50 dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800' };
  const typeIconBg = { vehicle: 'bg-cyan-100 dark:bg-cyan-900', asset: 'bg-amber-100 dark:bg-amber-900', task: 'bg-violet-100 dark:bg-violet-900', general: 'bg-zinc-100 dark:bg-zinc-800' };

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] overflow-hidden -mb-20 md:-mb-6" data-testid="reminders-page">
      <div className="shrink-0 p-4 md:px-8 md:pt-6 md:pb-4 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
        <div className="flex items-center justify-between">
          <div><h2 className="text-2xl sm:text-3xl font-medium tracking-tight">Reminders</h2><p className="text-sm text-zinc-500 mt-1">{total} reminder{total !== 1 ? 's' : ''}</p></div>
          <div className="flex bg-zinc-100 dark:bg-zinc-800 rounded-lg p-0.5 text-xs">
            {[['pending','Pending'],['done','Done'],['all','All']].map(([v,l]) => (
              <button key={v} onClick={() => setFilter(v)} className={`px-3 py-1.5 rounded-md font-medium ${filter === v ? 'bg-white dark:bg-zinc-700 shadow-sm text-zinc-900 dark:text-zinc-100' : 'text-zinc-500'}`}>{l}</button>
            ))}
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 md:px-8">
        <div className="w-full space-y-4">
          {loading ? <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>
          : groups.length === 0 ? <div className="flex flex-col items-center justify-center py-20"><AlarmClock className="h-10 w-10 text-zinc-300 mb-3" /><p className="text-zinc-500 text-sm">No reminders</p></div>
          : groups.map((group, gi) => {
            const Icon = typeIcons[group.group_type] || AlarmClock;
            const groupKey = `${group.group_type}:${group.reference_id}`;
            const isCollapsed = collapsed[groupKey];
            return (
              <div key={groupKey} className={`border rounded-2xl overflow-hidden ${typeColors[group.group_type] || typeColors.general}`} data-testid={`reminder-group-${gi}`}>
                <button onClick={() => toggleGroup(groupKey)} className="w-full flex items-center gap-3 p-4 text-left hover:opacity-80 transition-opacity">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${typeIconBg[group.group_type] || typeIconBg.general}`}>
                    <Icon className="h-4 w-4 text-zinc-600 dark:text-zinc-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{group.reference_name || 'General'}</p>
                    <p className="text-xs text-zinc-500">{group.group_type} · {group.reminders.length} reminder{group.reminders.length !== 1 ? 's' : ''}</p>
                  </div>
                  {isCollapsed ? <ChevronRight className="h-4 w-4 text-zinc-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-zinc-400 shrink-0" />}
                </button>
                {!isCollapsed && (
                  <div className="px-4 pb-4 space-y-2">
                    {group.reminders.map(r => (
                      <div key={r.id} className="flex items-center gap-3 bg-white dark:bg-zinc-900 rounded-xl px-3 py-2.5 border border-zinc-200 dark:border-zinc-800 group" data-testid={`reminder-item-${r.id}`}>
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${r.reminder_type === 'odometer' ? 'bg-blue-50 dark:bg-blue-950' : 'bg-amber-50 dark:bg-amber-950'}`}>
                          {r.reminder_type === 'odometer' ? <Gauge className="h-4 w-4 text-blue-500" /> : <Calendar className="h-4 w-4 text-amber-500" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{r.title || r.description || 'Reminder'}</p>
                          <p className="text-xs text-zinc-400">{r.reminder_type === 'odometer' ? `At ${r.target_km?.toLocaleString()} km` : r.reminder_datetime ? new Date(r.reminder_datetime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Badge variant="outline" className={`text-[10px] ${r.status === 'pending' ? 'border-amber-300 text-amber-600' : r.status === 'done' ? 'border-emerald-300 text-emerald-600' : 'border-blue-300 text-blue-600'}`}>{r.status}</Badge>
                          {r.status === 'pending' && <Button variant="ghost" size="sm" className="h-7 opacity-0 group-hover:opacity-100 text-emerald-600" onClick={() => markDone(r.id)}><Check className="h-3 w-3" /></Button>}
                          <Button variant="ghost" size="sm" className="h-7 opacity-0 group-hover:opacity-100 text-red-500" onClick={() => deleteReminder(r.id)}><Trash2 className="h-3 w-3" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
